import { AANAccountType, AccountTypes } from '@interfaces/models';
import { MultiITFrom } from '@interfaces/state';
import { getAccountName } from '@shared/helpers';
import {
  AccountRejectionReasons,
  AccountTypeMapping,
  accountTypeMapping,
  createFilteredAccount,
  eligibilityPermissionTypeCheck,
  eligibilityRoleTypeCheck,
  Eligible_Estate_Account_Types,
  Eligible_Minor_Account_Types,
  Eligible_Trust_Account_Types,
  FilteredClientAccount,
  getAccountNumber,
  getClientAccountsEntity,
  getServiceAgreement,
  IClientAccountsState,
  isClientPrimaryOwner,
  MarginCode,
  OptionPermission,
  ProductType,
  RejectedAccount,
  ServiceAgreement,
  SpecificAccountTypes,
} from '@store/reducers/client-accounts/client-accounts.reducer';

import { isCashPlusEligiblePosition } from '../multi-internal-transfer.helpers';

export const checkBalance = (balance: string, isTransferTo: boolean): boolean => {
  if (isTransferTo) return true;
  return Number(balance) > 0;
};

export const checkMarginAndOptions = (
  marginCode: MarginCode,
  optionPermission: OptionPermission,
  excludeMarginAndOptions: boolean, //included to support margin + options in future.
): boolean => {
  if (excludeMarginAndOptions) {
    if (
      marginCode === MarginCode.MARGIN ||
      (optionPermission && optionPermission !== OptionPermission.NO_PAPERS)
    )
      return false;
  }
  return true;
};

export const checkNonBrokerage = (productType: ProductType, isTransferTo: boolean): boolean => {
  if (!isTransferTo) return true;
  return productType === ProductType.BROKERAGE;
};

export const checkNonBrokerageAndNonMutualFund = (
  productType: ProductType,
  isTransferTo: boolean,
): boolean => {
  if (isTransferTo) return true;
  return productType === ProductType.BROKERAGE || productType === ProductType.MUTUAL_FUND;
};

export const checkAccountType = (accountType: string, targetAccountTypes: Set<string>): boolean => {
  return targetAccountTypes.has(accountType);
};

export const checkRoleType = (
  account: { accountType: AANAccountType },
  clientId: string,
  serviceAgreement: ServiceAgreement | null | undefined,
): boolean => {
  const isTrustAccount = Eligible_Trust_Account_Types.has(account.accountType);
  const isEstateAccount = Eligible_Estate_Account_Types.has(account.accountType);

  if (isTrustAccount || isEstateAccount) {
    const accountType: AccountTypes = isTrustAccount ? AccountTypes.TRUST : AccountTypes.ESTATE;
    return eligibilityRoleTypeCheck(accountType, clientId, serviceAgreement);
  }

  return true; // Pass if not Trust/Estate
};

export const checkPermissionType = (
  account: { accountType: AANAccountType },
  clientId: string,
  serviceAgreement: ServiceAgreement | null | undefined,
  isTransferTo: boolean,
): boolean => {
  if (!isTransferTo) return true;
  if (!Eligible_Minor_Account_Types.has(account.accountType)) return true;

  return eligibilityPermissionTypeCheck(clientId, serviceAgreement);
};

export const checkServiceAgreement = (
  serviceAgreement: ServiceAgreement | null | undefined,
): boolean => {
  return !!serviceAgreement;
};

export const checkPrimaryOwner = (
  account: { accountType: string },
  accountTypes: SpecificAccountTypes[],
  clientId: string,
  serviceAgreement: ServiceAgreement | null | undefined,
): boolean => {
  const verifyPrimaryOwner = shouldVerifyPrimaryOwnerForAccount(account, accountTypes);
  if (!verifyPrimaryOwner) return true;

  if (!serviceAgreement) return false;
  return isClientPrimaryOwner(clientId, serviceAgreement.registeredRoles);
};

export const shouldVerifyPrimaryOwnerForAccount = (
  account: { accountType: string },
  requestedTypes: SpecificAccountTypes[],
): boolean => {
  return requestedTypes.some((requestedType) => {
    // Check if this requested type requires primary owner verification
    const mapping = accountTypeMapping[requestedType];
    if (!mapping.verifyIsPrimaryOwner) return false;

    // Check if the account matches this requested type's pattern
    const typePattern = mapping.accountTypes;
    if (typePattern instanceof Set) {
      return typePattern.has(account.accountType);
    } else {
      // RegExp pattern
      return typePattern.test(account.accountType);
    }
  });
};

/**
 * Main eligibility validation function for Multi-IT
 * Uses the same validation order as the old validateAccountEligibility for consistency
 *
 * Multi-IT exclusion rules (with cashPlus and ITAdvised toggles = true):
 * - managed: false (always include managed accounts)
 * - marginAndOptions: true (always exclude margin/options)
 * - nonBrokerage: true for TO, false for FROM
 * - nonBrokerageAndNonMutualFund: true for FROM, false for TO
 * - cashPlus: false (always include cash plus accounts)
 */
export const getEligibleAccounts = (
  clientAccountState: IClientAccountsState,
  accountTypes: SpecificAccountTypes[],
  isTransferTo: boolean,
  rejectedAccounts: RejectedAccount[],
) => {
  const entity = getClientAccountsEntity(clientAccountState);
  const eligibleAccount: FilteredClientAccount = {
    vgClientPoid: null,
    accounts: [],
  };

  if (!entity?.vgClientPoid || !entity.accounts) {
    return eligibleAccount;
  }

  eligibleAccount.vgClientPoid = entity.vgClientPoid;
  const targetAccountTypes = getCombinedAccountTypeSet(accountTypes);
  const clientId = entity.vgClientPoid;

  entity.accounts.forEach((account) => {
    const serviceAgreement = getServiceAgreement(account, entity.serviceAgreements);

    // Helper to create and push rejected account
    const rejectAccount = (rejectionReason: AccountRejectionReasons) => {
      rejectedAccounts.push({
        accountId: account.accountId,
        accountName: account.accountName,
        accountType: account.accountType,
        formattedAccountName: getAccountName(
          {
            ...account,
            accountNumber: getAccountNumber(account),
            isBrokerage: account.productType === ProductType.BROKERAGE,
          },
          false,
        ),
        accountNumber: getAccountNumber(account),
        ...(account.nickname && { nickname: account.nickname }),
        rejectionReason,
      });
    };

    const checks = [
      {
        check: () => checkBalance(account.balance, isTransferTo),
        reason: AccountRejectionReasons.ZERO_BALANCE,
      },
      {
        check: () => checkMarginAndOptions(account.marginCode, account.optionPermission, true),
        reason: AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT,
      },
      {
        check: () => checkNonBrokerage(account.productType, isTransferTo),
        reason: AccountRejectionReasons.NON_BROKERAGE_ACCOUNT,
      },
      {
        check: () => checkNonBrokerageAndNonMutualFund(account.productType, isTransferTo),
        reason: AccountRejectionReasons.NON_BROKERAGE_OR_NON_MUTUAL_FUND_ACCOUNT,
      },
      {
        check: () => checkAccountType(account.accountType, targetAccountTypes),
        reason: AccountRejectionReasons.ACCOUNT_TYPE,
      },
      {
        check: () => checkRoleType(account, clientId, serviceAgreement),
        reason: AccountRejectionReasons.NON_MATCHING_ROLE_TYPE,
      },
      {
        check: () => checkPermissionType(account, clientId, serviceAgreement, isTransferTo),
        reason: AccountRejectionReasons.NON_MATCHING_PERMISSION_TYPE,
      },
      {
        check: () => checkServiceAgreement(serviceAgreement),
        reason: AccountRejectionReasons.INCORRECT_SERVICE_ACCOUNT_COUNT,
      },
      {
        check: () => checkPrimaryOwner(account, accountTypes, clientId, serviceAgreement),
        reason: AccountRejectionReasons.NOT_PRIMARY_OWNER,
      },
    ];

    for (const { check, reason } of checks) {
      if (!check()) {
        rejectAccount(reason);
        return;
      }
    }

    const filteredAccount = createFilteredAccount(account, serviceAgreement);
    if (filteredAccount) {
      eligibleAccount.accounts.push(filteredAccount);
    }
  });

  return eligibleAccount;
};

export const getCombinedAccountTypeSet = (accountTypes: SpecificAccountTypes[]): Set<string> => {
  const combinedSet = new Set<string>();

  accountTypes.forEach((accountType) => {
    const mapping: AccountTypeMapping = accountTypeMapping[accountType];
    const accountTypesPattern = mapping.accountTypes;

    if (accountTypesPattern instanceof Set) {
      // Add all values from the Set
      accountTypesPattern.forEach((value) => combinedSet.add(value));
    } else {
      // It's a RegExp - test all AANAccountType values against the pattern
      // and add any that match
      Object.values(AANAccountType).forEach((aanType) => {
        if (accountTypesPattern.test(aanType)) {
          combinedSet.add(aanType);
        }
      });
    }
  });

  return combinedSet;
};

export const fromPositionsHasMoneyMarketFund = (
  multiITFromState: MultiITFrom,
  sweepTransferEnabled = false,
) => {
  const fromPositions = multiITFromState?.selectedAccount?.accountData?.positions || [];
  return fromPositions.some((position) =>
    isCashPlusEligiblePosition(position, sweepTransferEnabled),
  );
};
